/**
 * AnnexPluginCard — pick a developer-published plugin and annex it into Fias
 * ownership (docs/plugin-annexation.md).
 *
 * Any developer plugin qualifies — draft or published; the annex only moves
 * ownership. The candidate list is the admin arches-summary restricted to
 * plugins with Fias-owned rows excluded in SQL, so its page is over annexable
 * rows. The server's tri-state `isPlatformOwned` is still honoured: `true` is
 * hidden (belt and braces), `null` (ownership could not be verified) or a
 * missing flag from an older API build stays visible with Annex disabled —
 * unknown is never read as "eligible", because the server would refuse the
 * annex anyway.
 *
 * A Dialog, not an AlertDialog: this collects a reason and a reference, so it
 * is a form with a confirm button rather than a yes/no confirmation, and the
 * fields sit beside the description instead of inside it.
 *
 * The confirm dialog's copy tracks what `annexPluginArche` actually does:
 * earnings cut over at the transfer (with the documented ≤1h cross-instance
 * cache drift), and the developer keeps Publish ONLY if they already hold an
 * active collaborator row.
 */

import * as React from 'react';
import { Search } from 'lucide-react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, type ArcheManagementSummary } from '@/lib/api';
import {
  ANNEX_CANDIDATE_LIMIT,
  useAnnexCandidates,
  useAnnexPlugin,
} from '@/contributor/hooks/usePlatformOwnedPlugins';

const REASON_MAX = 500;
const CONSENT_REF_MAX = 256;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown error';
}

function annexErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return 'This plugin is already owned by Fias. Refresh the list.';
    }
    if (error.status === 404) {
      return 'This plugin no longer exists.';
    }
  }
  return errorMessage(error);
}

interface AnnexPluginCardProps {
  /** Called with the arche id once an annex succeeds, so the list below can open its team panel. */
  onAnnexed: (archeId: string) => void;
}

export function AnnexPluginCard({ onAnnexed }: AnnexPluginCardProps) {
  const { toast } = useToast();
  const [search, setSearch] = React.useState('');
  const debounced = useDebouncedValue(search, 250);
  const candidates = useAnnexCandidates(debounced);
  const annex = useAnnexPlugin();

  const [target, setTarget] = React.useState<ArcheManagementSummary | null>(null);
  const [reason, setReason] = React.useState('');
  const [consentRef, setConsentRef] = React.useState('');
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const visible = React.useMemo(
    () => (candidates.data ?? []).filter((a) => a.isPlatformOwned !== true),
    [candidates.data],
  );

  const closeDialog = () => {
    setTarget(null);
    setReason('');
    setConsentRef('');
    setSubmitError(null);
  };

  const confirm = () => {
    if (!target) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setSubmitError('A reason is required; it is recorded in the audit trail.');
      return;
    }
    setSubmitError(null);
    annex.mutate(
      {
        archeId: target.archId,
        reason: trimmedReason,
        ...(consentRef.trim() ? { consentRef: consentRef.trim() } : {}),
      },
      {
        onSuccess: (result) => {
          toast({ title: `Annexed ${target.name}`, description: 'Fias now owns this plugin.' });
          onAnnexed(result.archeId);
          setSearch('');
          closeDialog();
        },
        onError: (error) => setSubmitError(annexErrorText(error)),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Annex a plugin</CardTitle>
        <CardDescription>
          Convert any developer plugin — draft or published — into a Fias-owned one, in place: same
          arche id, slug, bundle, installs and ratings. Only ownership changes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="annex-search">Find a developer plugin</Label>
          <div className="relative max-w-md">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="annex-search"
              className="pl-9"
              placeholder="Name, arche id, or contributor"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {candidates.isLoading ? (
          <div className="space-y-2" data-testid="annex-candidates-loading">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : candidates.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load plugins: {errorMessage(candidates.error)}
          </p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {debounced.trim()
              ? 'No developer plugins match.'
              : 'No developer-owned plugins to annex.'}
          </p>
        ) : (
          <ul className="divide-y rounded border">
            {visible.map((plugin) => {
              const unverified = plugin.isPlatformOwned == null;
              return (
                <li
                  key={plugin.archId}
                  className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{plugin.name}</span>
                      <Badge variant="secondary">{plugin.status}</Badge>
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">{plugin.archId}</div>
                    <div className="text-xs text-muted-foreground">
                      by {plugin.contributorUsername ?? 'an unknown contributor'}
                    </div>
                    {unverified && (
                      <p className="text-xs text-destructive">
                        Ownership could not be verified; try again shortly.
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={unverified}
                    onClick={() => setTarget(plugin)}
                  >
                    Annex
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        {(candidates.data?.length ?? 0) >= ANNEX_CANDIDATE_LIMIT && (
          <p className="text-xs text-muted-foreground">
            Showing the first {ANNEX_CANDIDATE_LIMIT} eligible plugins. Search to find one that is
            not listed.
          </p>
        )}
      </CardContent>

      <Dialog open={target !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annex {target?.name}?</DialogTitle>
            <DialogDescription>
              Currently owned by {target?.contributorUsername ?? 'an unknown contributor'}. Draft or
              published, only ownership changes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Fias becomes the maker and financial owner. New earnings route to Fias from the
                cutover; an API instance with a warm ownership cache can still credit the previous
                developer for up to an hour.
              </li>
              <li>
                If the developer currently holds an active collaborator row it is demoted to Publish
                — they keep <code>fias-dev submit</code> access and lose team management. If they
                hold no active row, nothing is granted; add them below afterwards if they should
                keep maintaining it.
              </li>
              <li>This cannot be reversed from this page.</li>
            </ul>
            <div className="space-y-1">
              <Label htmlFor="annex-reason">Reason (required)</Label>
              <Textarea
                id="annex-reason"
                value={reason}
                maxLength={REASON_MAX}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Recorded in the audit trail"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="annex-consent">Consent / agreement reference (optional)</Label>
              <Input
                id="annex-consent"
                value={consentRef}
                maxLength={CONSENT_REF_MAX}
                onChange={(e) => setConsentRef(e.target.value)}
                placeholder="Employment term, ticket, or transfer agreement"
              />
            </div>
            {submitError && (
              <p role="alert" className="text-destructive">
                {submitError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={annex.isPending} onClick={closeDialog}>
              Cancel
            </Button>
            <Button disabled={annex.isPending} onClick={confirm}>
              {annex.isPending ? 'Annexing…' : 'Annex plugin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
